import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, Coins, Copy, Check, Loader2 } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { supabase } from "@/integrations/supabase/client";
import { isPaymentUiBlocked } from "@/lib/platform";
import { analytics } from "@/lib/analytics";
import { metaTrack } from "@/lib/metaPixel";
import {
  CREDIT_PACKS,
  formatBRL,
  unitsToTextMessages,
  unitsToVoiceMinutes,
  type CreditPack,
} from "@/config/credits";

type View = "loading" | "packs" | "customer" | "pix" | "confirming" | "expired" | "success";

interface PixData {
  encodedImage: string;
  payload: string;
  expirationDate?: string;
}

// Persist the in-flight PIX so a pending purchase survives unmount / reload /
// app backgrounding. credit_purchases (DB) is the source of truth for STATUS;
// this only carries the QR + expiry that the backend doesn't store.
const PENDING_KEY = "vyv.credits.pending";
// Fallback resume window when no PIX expirationDate is known (e.g. localStorage
// was cleared but a pending row still exists): don't resume rows older than this.
const PENDING_FALLBACK_WINDOW_MS = 30 * 60 * 1000;

interface StoredPending {
  purchaseId: string;
  pix: PixData;
  expirationDate?: string;
}

function savePending(p: StoredPending): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify(p));
  } catch (e) {
    console.error("[CreditsPage] failed to persist pending purchase:", e);
  }
}

function readPending(): StoredPending | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as StoredPending) : null;
  } catch (e) {
    console.error("[CreditsPage] failed to read pending purchase:", e);
    return null;
  }
}

function clearPending(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch (e) {
    console.error("[CreditsPage] failed to clear pending purchase:", e);
  }
}

// Expired only when we can prove it: a parsed PIX expirationDate in the past,
// or (no expiry known) a pending row older than the fallback window. When we
// can't tell, return false and keep polling — never show "expired" on a guess.
function isPendingExpired(expirationDate: string | undefined, createdAt: string | undefined): boolean {
  if (expirationDate) {
    // Asaas returns "YYYY-MM-DD HH:mm:ss"; normalize the space to "T" for Date.
    const parsed = new Date(expirationDate.replace(" ", "T"));
    if (!isNaN(parsed.getTime())) return Date.now() > parsed.getTime();
  }
  if (createdAt) {
    const created = new Date(createdAt);
    if (!isNaN(created.getTime())) return Date.now() > created.getTime() + PENDING_FALLBACK_WINDOW_MS;
  }
  return false;
}

const CreditsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, tf } = useLanguage();

  // Paywall placement handed over by PaywallSheet's credits CTA (navigation
  // state) — stamps checkout_started.source. Absent = organic /creditos visit.
  const paywallSource = (location.state as { paywallPlacement?: string } | null)?.paywallPlacement;

  const [view, setView] = useState<View>("loading");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [selectedPack, setSelectedPack] = useState<CreditPack | null>(null);

  // Customer form (only shown when the checkout reports customer_required).
  const [name, setName] = useState("");
  const [cpf, setCpf] = useState("");

  const [pix, setPix] = useState<PixData | null>(null);
  const [purchaseId, setPurchaseId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startPolling = useCallback((pid: string, packId?: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const { data, error: readErr } = await supabase
        .from("credit_purchases")
        .select("status")
        .eq("id", pid)
        .maybeSingle();
      // A read failure is transient — log it and let the next tick retry. It
      // must NEVER surface as a checkout error: the payment may be succeeding
      // server-side while a single poll read hiccups.
      if (readErr) {
        console.error("[CreditsPage] poll read failed:", readErr);
        return;
      }
      const status = (data as { status?: string } | null)?.status;
      if (status === "paid") {
        if (pollRef.current) clearInterval(pollRef.current);
        clearPending();
        // Funnel closer: client-observed PIX confirmation. Fires once — the
        // interval is cleared on this transition. On the post-reload resume
        // path `paywallSource` is gone (navigation state doesn't survive
        // reload) and the event goes out unattributed — honest; the
        // server-side purchase truth stays with the webhook.
        analytics.checkoutCompleted("credits", paywallSource, packId);
        setView("success");
      } else if (status === "failed") {
        if (pollRef.current) clearInterval(pollRef.current);
        clearPending();
        setError(t("credits.checkout.failed"));
        setView("packs");
      }
    }, 4000);
  }, [t, paywallSource]);

  // Resume a pending purchase from the DB (source of truth) so confirmation
  // survives unmount / reload / app backgrounding. Returns true when a pending
  // purchase was found and a confirmation view was shown.
  const resumePendingPurchase = useCallback(async (): Promise<boolean> => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;
    const { data, error: lookupErr } = await supabase
      .from("credit_purchases")
      .select("id, status, created_at, pack_id")
      .eq("user_id", user.id)
      .eq("status", "pending")
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    if (lookupErr) {
      console.error("[CreditsPage] pending lookup failed:", lookupErr);
      return false;
    }
    const row = data as { id: string; status: string; created_at: string; pack_id: string | null } | null;
    if (!row) return false;

    const stored = readPending();
    const sameStored = stored && stored.purchaseId === row.id ? stored : null;

    if (isPendingExpired(sameStored?.expirationDate, row.created_at)) {
      clearPending();
      setError(null);
      setView("expired");
      return true;
    }

    setError(null);
    setPurchaseId(row.id);
    if (sameStored?.pix) {
      // QR still cached locally — show it again so the user can finish paying.
      setPix(sameStored.pix);
      setView("pix");
    } else {
      // Pending row exists but no local QR (reload cleared in-memory state):
      // we can't re-render the code, but we can still confirm the payment.
      setView("confirming");
    }
    startPolling(row.id, row.pack_id ?? undefined);
    return true;
  }, [startPolling]);

  // iOS gate — exactly like /pricing. Apple 3.1.1: no purchase UI in-app.
  useEffect(() => {
    if (isPaymentUiBlocked()) {
      navigate("/home", { replace: true });
      return;
    }
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate("/auth?redirect=/creditos", { replace: true });
        return;
      }
      const resumed = await resumePendingPurchase();
      if (!resumed) setView("packs");
    })();
  }, [navigate, resumePendingPurchase]);

  // Re-check the pending purchase whenever the tab/app returns to the
  // foreground (user coming back from their bank app after paying the PIX).
  // @capacitor/app is not installed, so we use the web visibility/focus
  // events — these also fire inside the Android Capacitor webview on resume.
  useEffect(() => {
    const recheck = () => {
      if (document.visibilityState === "visible") void resumePendingPurchase();
    };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => {
      document.removeEventListener("visibilitychange", recheck);
      window.removeEventListener("focus", recheck);
    };
  }, [resumePendingPurchase]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const checkout = async (pack: CreditPack, withCustomer: boolean) => {
    setSubmitting(true);
    setError(null);
    // The credits web checkout emitted NOTHING before 12/07 — this is the
    // moment the payment flow actually starts (PIX charge about to be
    // created). Not re-fired on the customer_required retry leg: withCustomer
    // means the same purchase attempt continuing, not a new funnel entry.
    if (!withCustomer) {
      analytics.checkoutStarted("credits", paywallSource, pack.id);
      // Meta mirror — same trigger, PostHog untouched.
      metaTrack("InitiateCheckout", {
        content_category: "credits",
        content_ids: [pack.id],
        content_type: "product",
        currency: "BRL",
        value: pack.amountBrl,
      });
    }
    try {
      const body: Record<string, unknown> = { pack_id: pack.id };
      if (withCustomer) {
        body.name = name.trim();
        body.cpf = cpf.replace(/\D/g, "");
      }
      const { data, error: fnErr } = await supabase.functions.invoke("credits-checkout", { body });
      if (fnErr) {
        // FunctionsHttpError → read the JSON body for customer_required.
        let code = "";
        try {
          const ctx = (fnErr as { context?: Response }).context;
          if (ctx && typeof ctx.json === "function") code = ((await ctx.json()) as { error?: string })?.error || "";
        } catch (e) { console.error("[CreditsPage] failed to parse fn error body:", e); }
        if (code === "customer_required") {
          setSelectedPack(pack);
          setView("customer");
          setSubmitting(false);
          return;
        }
        setError(t("credits.checkout.error"));
        setSubmitting(false);
        return;
      }
      const res = data as { pix?: PixData; purchase_id?: string };
      if (!res?.pix || !res?.purchase_id) {
        setError(t("credits.checkout.error"));
        setSubmitting(false);
        return;
      }
      setPix(res.pix);
      setPurchaseId(res.purchase_id);
      savePending({ purchaseId: res.purchase_id, pix: res.pix, expirationDate: res.pix.expirationDate });
      setView("pix");
      startPolling(res.purchase_id, pack.id);
    } catch (e) {
      console.error("[CreditsPage] checkout failed:", e);
      setError(t("credits.checkout.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const copyPayload = async () => {
    if (!pix?.payload) return;
    try {
      await navigator.clipboard.writeText(pix.payload);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard blocked — user can long-press the field */ }
  };

  if (isPaymentUiBlocked()) return null;

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#0a0a0f" }}>
      <button
        onClick={() => navigate(-1)}
        aria-label="Close"
        className="fixed right-4 z-50 w-9 h-9 rounded-full flex items-center justify-center"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 1rem)",
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <X size={16} className="text-white/60" />
      </button>

      <div
        className="relative z-10 px-5 pb-20 max-w-2xl mx-auto"
        style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 3rem)" }}
      >
        <div className="flex items-center gap-2 mb-2">
          <Coins size={18} style={{ color: "hsl(45 90% 60%)" }} />
          <span className="text-[10px] font-display tracking-[0.25em] uppercase" style={{ color: "hsl(45 90% 55% / 0.8)" }}>
            {t("credits.title")}
          </span>
        </div>
        <h1 className="font-display text-[26px] font-bold text-white leading-tight mb-2">
          {t("credits.headline")}
        </h1>
        <p className="text-[13px] font-body mb-6" style={{ color: "hsl(210 20% 65%)" }}>
          {t("credits.subtitle")}
        </p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl text-[13px]" style={{ background: "hsl(0 70% 50% / 0.12)", border: "1px solid hsl(0 70% 50% / 0.25)", color: "hsl(0 70% 75%)" }}>
            {error}
          </div>
        )}

        {view === "loading" && (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white/40" size={24} /></div>
        )}

        {view === "packs" && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {CREDIT_PACKS.map((pack) => (
              <button
                key={pack.id}
                disabled={submitting}
                onClick={() => checkout(pack, false)}
                className="rounded-2xl p-5 text-left transition-all active:scale-[0.97] disabled:opacity-50"
                style={{ background: "rgba(255,255,255,0.03)", border: "1.5px solid hsl(45 90% 55% / 0.4)" }}
              >
                <div className="font-display text-[22px] font-bold text-white mb-1">{formatBRL(pack.amountBrl)}</div>
                <div className="text-[12px] font-body" style={{ color: "hsl(45 90% 65%)" }}>
                  {tf("credits.pack.credits", { n: String(pack.credits) })}
                </div>
                <div className="text-[11px] font-body mt-2 leading-relaxed" style={{ color: "hsl(210 20% 60%)" }}>
                  {tf("credits.pack.equiv", { min: String(unitsToVoiceMinutes(pack.units)), msgs: String(unitsToTextMessages(pack.units)) })}
                </div>
              </button>
            ))}
          </div>
        )}

        {view === "customer" && selectedPack && (
          <div className="rounded-2xl p-5 space-y-3" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <p className="text-[13px] font-body" style={{ color: "hsl(210 20% 70%)" }}>
              {t("credits.customer.prompt")}
            </p>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("credits.customer.name")}
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
            />
            <input
              value={cpf}
              onChange={(e) => setCpf(e.target.value)}
              inputMode="numeric"
              placeholder={t("credits.customer.cpf")}
              className="w-full px-3 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-white text-[14px] placeholder:text-white/30 focus:outline-none focus:border-amber-400/40"
            />
            <button
              disabled={submitting || name.trim().length < 3 || cpf.replace(/\D/g, "").length !== 11}
              onClick={() => checkout(selectedPack, true)}
              className="w-full py-3 rounded-xl font-display text-[13px] font-bold text-[#1a1208] disabled:opacity-40"
              style={{ background: "linear-gradient(135deg, hsl(45 90% 50%), hsl(45 90% 60%))" }}
            >
              {submitting ? t("credits.checkout.creating") : t("credits.customer.continue")}
            </button>
          </div>
        )}

        {view === "pix" && pix && (
          <div className="rounded-2xl p-5 flex flex-col items-center text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <p className="text-[13px] font-display font-semibold text-white mb-3">{t("credits.pix.title")}</p>
            {pix.encodedImage && (
              <img
                src={"data:image/png;base64," + pix.encodedImage}
                alt="PIX QR"
                className="w-48 h-48 rounded-xl bg-white p-2 mb-3"
              />
            )}
            <button
              onClick={copyPayload}
              className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[12px] font-display font-semibold text-white mb-2"
              style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? t("credits.pix.copied") : t("credits.pix.copy")}
            </button>
            <p className="text-[11px] font-body flex items-center gap-1.5 mt-1" style={{ color: "hsl(210 20% 55%)" }}>
              <Loader2 className="animate-spin" size={12} /> {t("credits.pix.waiting")}
            </p>
          </div>
        )}

        {view === "confirming" && (
          <div className="rounded-2xl p-6 flex flex-col items-center text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}>
            <Loader2 className="animate-spin text-white/50 mb-3" size={26} />
            <p className="font-display text-[16px] font-bold text-white mb-1">{t("credits.confirming.title")}</p>
            <p className="text-[12px] font-body" style={{ color: "hsl(210 20% 70%)" }}>{t("credits.confirming.body")}</p>
          </div>
        )}

        {view === "expired" && (
          <div className="rounded-2xl p-6 flex flex-col items-center text-center" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid hsl(210 20% 40% / 0.25)" }}>
            <p className="font-display text-[16px] font-bold text-white mb-1">{t("credits.expired.title")}</p>
            <p className="text-[12px] font-body mb-4" style={{ color: "hsl(210 20% 70%)" }}>{t("credits.expired.body")}</p>
            <button
              onClick={() => { setError(null); setPix(null); setPurchaseId(null); setView("packs"); }}
              className="px-5 py-2.5 rounded-xl font-display text-[13px] font-bold text-[#1a1208]"
              style={{ background: "linear-gradient(135deg, hsl(45 90% 50%), hsl(45 90% 60%))" }}
            >
              {t("credits.expired.cta")}
            </button>
          </div>
        )}

        {view === "success" && (
          <div className="rounded-2xl p-6 flex flex-col items-center text-center" style={{ background: "hsl(140 60% 40% / 0.10)", border: "1px solid hsl(140 60% 45% / 0.35)" }}>
            <Check size={28} className="text-green-400 mb-2" />
            <p className="font-display text-[16px] font-bold text-white mb-1">{t("credits.success.title")}</p>
            <p className="text-[12px] font-body mb-4" style={{ color: "hsl(210 20% 70%)" }}>{t("credits.success.body")}</p>
            <button
              onClick={() => navigate("/home")}
              className="px-5 py-2.5 rounded-xl font-display text-[13px] font-bold text-[#06140d]"
              style={{ background: "linear-gradient(135deg, hsl(140 60% 50%), hsl(140 60% 60%))" }}
            >
              {t("credits.success.cta")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditsPage;
